import {
  Activity,
  cloneElement,
  createElement,
  ElementType,
  isValidElement,
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Button } from "./button"
import {
  AnchorContext,
  AnchorItem,
  AnchorItemContext,
  ScrollingAnchorActiveKeyContext,
  ScrollingAnchorContext,
  ScrollingAnchorItemContext,
  useAnchorContext,
  useAnchorItemContext,
  useScrollingAnchorActiveKeyContext,
  useScrollingAnchorContext,
  useScrollingAnchorItemContext,
} from "./anchor-hooks"
import { flushSync } from "react-dom"
import { cn } from "@/lib/utils"
import { CardContent } from "./card"

export default function Anchor({ children }: { children: ReactNode }) {
  return (
    <HashAnchorProvider>
      <ScrollingAnchorProvider>{children}</ScrollingAnchorProvider>
    </HashAnchorProvider>
  )
}

Anchor.HashLink = HashAnchorLink
Anchor.HashSection = HashAnchorSection
Anchor.HashTitle = HashAnchorTitle
Anchor.HashItemProvider = HashAnchorItemProvider
Anchor.HashCardContent = HashAnchorCardContent
Anchor.ScrollingLink = ScrollingAnchorLink
Anchor.ScrollingSection = memo(ScrollingAnchorSection)
Anchor.ScrollingTitle = memo(ScrollingAnchorTitle)
Anchor.ScrollingVisibilityRoot = ScrollingAnchorVisibilityRoot
Anchor.ScrollingCardContent = ScrollingAnchorCardContent

export function HashAnchorProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<AnchorItem[]>([])
  const [activeKey, setActiveKey] = useState<string>()

  const updateActiveKey = (items: AnchorItem[]) => {
    const activeItem = items[0]
    if (activeItem) {
      setActiveKey(activeItem.sectionKey)
    }
  }

  const registerItem = useCallback((item: AnchorItem) => {
    setItems((prevItems) => {
      const newItems = [...prevItems, item]
      updateActiveKey(newItems)
      return newItems
    })
  }, [])

  const unregisterItem = useCallback((key: string) => {
    setItems((prevItems) => {
      const newItems = prevItems.filter((item) => item.sectionKey !== key)
      updateActiveKey(newItems)
      return newItems
    })
  }, [])

  const onNavigate = useCallback((key: string) => {
    setActiveKey(key)
  }, [])

  const anchorContextValue = {
    registerItem,
    unregisterItem,
    activeKey,
    items,
    onNavigate,
  }

  return (
    <AnchorContext.Provider value={anchorContextValue}>
      {children}
    </AnchorContext.Provider>
  )
}

export function HashAnchorItemProvider({
  sectionKey,
  children,
}: {
  sectionKey: string
  children: ReactNode
}) {
  const { items, onNavigate } = useAnchorContext()
  const item = items.find((item) => item.sectionKey === sectionKey)

  if (!item) {
    return null
  }

  return (
    <AnchorItemContext.Provider
      value={{ ...item, onNavigate: () => onNavigate(sectionKey) }}
    >
      {children}
    </AnchorItemContext.Provider>
  )
}

export function HashAnchorSection({
  sectionKey,
  children,
}: {
  sectionKey: string
  children: ReactNode
}) {
  const { activeKey } = useAnchorContext()

  return (
    <Activity mode={activeKey === sectionKey ? "visible" : "hidden"}>
      <HashAnchorItemProvider sectionKey={sectionKey}>
        {children}
      </HashAnchorItemProvider>
    </Activity>
  )
}

export function HashAnchorLink(
  props: AnchorItem & {
    render: (renderArgs: {
      title: string
      active: boolean
      onNavigate: () => void
    }) => React.ReactNode
  },
): React.ReactNode

export function HashAnchorLink(
  props: AnchorItem & React.ComponentProps<typeof Button>,
): React.ReactNode

export function HashAnchorLink({
  title,
  sectionKey,
  render,
  className,
  ...props
}: AnchorItem &
  (React.ComponentProps<typeof Button> & {
    render?: (renderArgs: {
      title: string
      active: boolean
      onNavigate: () => void
    }) => React.ReactNode
  })) {
  const { activeKey, registerItem, unregisterItem, onNavigate } =
    useAnchorContext()
  useEffect(() => {
    registerItem({ sectionKey, title })
    return () => unregisterItem(sectionKey)
  }, [sectionKey, title, registerItem, unregisterItem])

  const active = activeKey === sectionKey

  if (render) {
    return render({ active, title, onNavigate: () => onNavigate(sectionKey) })
  }

  return (
    <Button
      variant="link"
      role="link"
      data-testid="anchor-hash-link"
      className={cn(
        className,
        active ? "text-primary" : "",
        "text-md font-semibold",
      )}
      onClick={(e) => {
        e.preventDefault()
        onNavigate(sectionKey)
      }}
      {...props}
    >
      {title}
    </Button>
  )
}

export function HashAnchorTitle<TElement extends ElementType = "span">(
  props: { as?: TElement; render?: never } & React.ComponentProps<TElement>,
): React.ReactNode
export function HashAnchorTitle(props: {
  render?: React.FC<{ title: string }>
}): React.ReactNode

export function HashAnchorTitle<TElement extends ElementType = "span">({
  as,
  render,
  ...props
}: {
  as?: TElement
  render?: React.FC<{ title: string }>
} & React.ComponentProps<TElement>) {
  const { title } = useAnchorItemContext()

  if (as) {
    return createElement(as, props, title)
  }

  if (render) {
    return render({ title })
  }

  return title
}

export function ScrollingAnchorProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [items, setItems] = useState<AnchorItem[]>([])
  const itemElements = useRef<Map<string, Element>>(new Map())
  const observer = useRef<IntersectionObserver | null>(null)
  const [activeKeys, setActiveKeys] = useState<string[]>([])
  const activeKey = activeKeys[0]

  useEffect(() => {
    return () => {
      observer.current?.disconnect()
      observer.current = null
    }
  }, [])

  const visibilityRootRef = useCallback(
    (node: HTMLElement | null) => {
      if (node && !observer.current) {
        observer.current = new IntersectionObserver(
          (entries) => {
            setActiveKeys((prevActiveKeys) => {
              const nextActiveKeysSet: Set<string> = new Set()

              entries
                .filter((entry) => entry.isIntersecting)
                .map((entry) => entry.target.id)
                .forEach((id) => nextActiveKeysSet.add(id))

              prevActiveKeys.forEach((key) => {
                if (!entries.some((entry) => entry.target.id === key)) {
                  nextActiveKeysSet.add(key)
                }
              })

              const nextActiveKeys = Array.from(itemElements.current.values())
                .sort(
                  (a, b) =>
                    a.getBoundingClientRect().top -
                    b.getBoundingClientRect().top,
                )
                .map((element) => element.id)
                .filter((id) => nextActiveKeysSet.has(id))

              return nextActiveKeys
            })
          },
          { threshold: 0.1, root: node },
        )
      }
    },
    [observer],
  )

  const registerItem = useCallback((item: AnchorItem) => {
    setItems((prevItems) => [...prevItems, item])
  }, [])

  const unregisterItem = useCallback((key: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.sectionKey !== key))
  }, [])

  const registerItemElement = useCallback(
    (sectionKey: string, element: Element) => {
      itemElements.current.set(sectionKey, element)
      observer.current?.observe(element)
    },
    [],
  )

  const unregisterItemElement = useCallback((sectionKey: string) => {
    const element = itemElements.current.get(sectionKey)
    if (element) {
      observer.current?.unobserve(element)
    }
    itemElements.current.delete(sectionKey)
    setActiveKeys((prevActiveKeys) =>
      prevActiveKeys.filter((key) => key !== sectionKey),
    )
  }, [])

  const onNavigate = useCallback(
    (key: string, onAnchorNavigate: () => void) => {
      const item = itemElements.current.get(key)
      if (item) {
        item.scrollIntoView({ block: "start" })
      } else {
        flushSync(() => onAnchorNavigate())
        window.requestAnimationFrame(() => {
          const item = itemElements.current.get(key)
          if (item) {
            item.scrollIntoView({ block: "start" })
          }
        })
      }
    },
    [itemElements],
  )

  const contextValue = useMemo(
    () => ({
      items,
      registerItem,
      unregisterItem,
      registerItemElement,
      unregisterItemElement,
      visibilityRootRef,
      onNavigate,
    }),
    [
      items,
      registerItem,
      unregisterItem,
      registerItemElement,
      unregisterItemElement,
      visibilityRootRef,
      onNavigate,
    ],
  )

  const activeKeyContextValue = useMemo(
    () => ({
      activeKey,
    }),
    [activeKey],
  )

  return (
    <ScrollingAnchorContext.Provider value={contextValue}>
      <ScrollingAnchorActiveKeyContext.Provider value={activeKeyContextValue}>
        {children}
      </ScrollingAnchorActiveKeyContext.Provider>
    </ScrollingAnchorContext.Provider>
  )
}

function ScrollingAnchorItemProvider({
  sectionKey,
  children,
}: {
  sectionKey: string
  children: ReactNode
}) {
  const { items, onNavigate } = useScrollingAnchorContext()
  const { onNavigate: onAnchorNavigate } = useAnchorItemContext()

  const item = items.find((item) => item.sectionKey === sectionKey)

  if (!item) {
    return null
  }

  return (
    <ScrollingAnchorItemContext.Provider
      value={{
        title: item.title,
        sectionKey: item.sectionKey,
        onNavigate: () => onNavigate(sectionKey, onAnchorNavigate),
      }}
    >
      {children}
    </ScrollingAnchorItemContext.Provider>
  )
}

function ScrollingAnchorSection<TElement extends ElementType = "div">({
  sectionKey,
  children,
  as,
  ...props
}: {
  sectionKey: string
  children: ReactNode
  as?: TElement
} & React.ComponentPropsWithoutRef<TElement>) {
  const { registerItemElement, unregisterItemElement } =
    useScrollingAnchorContext()

  return createElement(
    as || "div",
    {
      ...props,
      ref: (element: HTMLElement) => {
        element && registerItemElement(sectionKey, element)
        return () => {
          unregisterItemElement(sectionKey)
        }
      },
      id: sectionKey,
    },
    <ScrollingAnchorItemProvider sectionKey={sectionKey}>
      {children}
    </ScrollingAnchorItemProvider>,
  )
}

export function ScrollingAnchorLink({
  sectionKey,
  title,
}: {
  sectionKey: string
  title: string
}) {
  const { onNavigate: onAnchorNavigate } = useAnchorItemContext()
  const { registerItem, unregisterItem, onNavigate } =
    useScrollingAnchorContext()
  const { activeKey } = useScrollingAnchorActiveKeyContext()

  useEffect(() => {
    registerItem({ sectionKey, title })
    return () => unregisterItem(sectionKey)
  }, [registerItem, sectionKey, title, unregisterItem])

  return (
    <Button
      variant="link"
      role="link"
      data-testid="anchor-scroll-link"
      onClick={(e) => {
        e.preventDefault()
        onNavigate(sectionKey, onAnchorNavigate)
      }}
      className={cn(
        activeKey === sectionKey ? "text-primary" : "",
        // A section's entries sit under its semibold heading; at the control
        // weight they would match it and the nav would lose its hierarchy.
        "text-md ml-2 h-min py-0 font-normal wrap-normal",
      )}
      key={sectionKey}
    >
      {title}
    </Button>
  )
}

export function ScrollingAnchorVisibilityRoot({
  asChild,
  children,
  ...props
}: React.ComponentPropsWithRef<ElementType> & {
  asChild?: boolean
  children: ReactElement<React.ComponentPropsWithRef<ElementType>>
}) {
  const { visibilityRootRef } = useScrollingAnchorContext()
  if (asChild && isValidElement(children)) {
    return cloneElement(children, {
      ref: (node: HTMLElement | null) => {
        visibilityRootRef(node)
      },
      ...props,
    })
  }

  return (
    <div ref={visibilityRootRef} {...props}>
      {children}
    </div>
  )
}

export function ScrollingAnchorTitle<TElement extends ElementType = "span">(
  props: { as?: TElement; render?: never } & React.ComponentProps<TElement>,
): ReactNode

export function ScrollingAnchorTitle<
  TElement extends ElementType = "span",
>(props: { as?: TElement; render?: React.FC<{ title: string }> }): ReactNode

export function ScrollingAnchorTitle<TElement extends ElementType = "span">({
  as,
  render,
  ...props
}: {
  as?: TElement
  render?: React.FC<{ title: string }>
} & React.ComponentProps<TElement>): ReactNode {
  const { title } = useScrollingAnchorItemContext()

  return createElement(
    as || "span",
    {
      ...props,
    },
    render ? render({ title }) : title,
  )
}

export function ScrollingAnchorCardContent(
  props: React.ComponentProps<typeof CardContent>,
) {
  const { title, sectionKey } = useScrollingAnchorItemContext()
  return (
    <CardContent
      data-sectionkey={sectionKey}
      data-sectiontitle={title}
      {...props}
    />
  )
}

export function HashAnchorCardContent(
  props: React.ComponentProps<typeof CardContent>,
) {
  const { title, sectionKey } = useAnchorItemContext()
  return (
    <CardContent
      data-sectionkey={sectionKey}
      data-sectiontitle={title}
      {...props}
    />
  )
}
